import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { orgStorage } from "@/shared/auth";

import type { FeatureFlagName } from "../FeatureFlagName";

import { featureFlagQueryKeys } from "./featureFlagQueryKeys";

/**
 * Returns the caller's feature flag states, keyed by org ID and then flag
 * name. Covers every org the caller belongs to, plus the current org when the
 * caller can access it without membership (a Roboto admin viewing another
 * org).
 */
export function useFeatureFlagStates() {
  const { featureFlagService } = useDomainServices();

  return useQuery<Record<string, Record<string, boolean>>, Error>({
    queryKey: featureFlagQueryKeys.states(),
    // Read the org from storage when the fetch runs. Switching orgs writes
    // storage and invalidates this query before the next render, so an org ID
    // captured at render time would refetch the previous org's flags.
    queryFn: ({ signal }) =>
      featureFlagService.checkAllFeatureFlags({
        abortSignal: signal,
        orgId: orgStorage.getCurrentOrg()?.org_id,
      }),
    // Flags should be frozen for the session to make a predictable experience
    staleTime: Infinity,
  });
}

/**
 * Check whether a single feature flag is enabled for the current user, within
 * `orgId` — defaulting to the current org.
 */
export function useCheckFeatureFlag(
  flagName: FeatureFlagName,
  orgId?: string,
): boolean {
  const resolvedOrg = useCurrentOrgId(orgId);
  const { data } = useFeatureFlagStates();

  // Should practically never default to false because we prefetch at the auth layer
  return data?.[resolvedOrg]?.[flagName] ?? false;
}
